import { CloseIcon, theme } from '@arnold/common';
import styled from '@emotion/styled';
import type { ReactNode } from 'react';
import { Modal } from 'react-bootstrap';

type Size = 'small' | 'large' | 'full';

/** Shared dialog chrome for the report editor. Bootstrap's `close` styles are not bundled, so the header owns its button. */
export function EditorModal({
  title,
  size = 'small',
  onClose,
  footer,
  children,
  enforceFocus = true,
}: {
  title: ReactNode;
  size?: Size;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  enforceFocus?: boolean;
}) {
  return (
    <Dialog show onHide={onClose} backdrop="static" keyboard={false} enforceFocus={enforceFocus} variant={size}>
      <Header>
        <Title>{title}</Title>
        <CloseButton type="button" onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <Body grey={size === 'full'}>{children}</Body>
      {footer && <Footer>{footer}</Footer>}
    </Dialog>
  );
}

const WIDTHS: Record<Size, string> = { small: '480px', large: '760px', full: '90vw' };
const Dialog = styled(Modal, { shouldForwardProp: (prop) => prop !== 'variant' })<{ variant: Size }>`
  /* Dialogs scroll inside their body; the overlay itself never needs a scrollbar. */
  overflow-y: hidden;
  .modal-dialog {
    display: flex;
    align-items: center;
    width: ${({ variant }) => WIDTHS[variant]};
    max-width: ${({ variant }) => (variant === 'full' ? '90vw' : `min(${WIDTHS[variant]}, 95vw)`)};
    min-height: 100%;
    margin: 0 auto;
  }
  .modal-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: ${({ variant }) => (variant === 'full' ? '90vh' : 'auto')};
    max-height: 90vh;
    min-width: 0;
    overflow: hidden;
    border: 0;
    border-radius: 12px;
    color: ${theme.colors.text.primary};
    font-size: 13px;
  }
  * {
    box-sizing: border-box;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
`;
const Title = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  overflow-wrap: anywhere;
`;
const CloseButton = styled.button`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  svg {
    width: 16px;
    height: 16px;
  }
  svg path {
    fill: ${theme.colors.text.secondary};
  }
  &:hover {
    background: ${theme.colors.backgroundBasic.active};
  }
`;
const Body = styled.div<{ grey: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 20px 24px;
  background: ${({ grey }) => (grey ? theme.colors.backgroundBasic.default : theme.colors.backgroundCover.default)};
  --new-report-header-height: 0px;
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  padding: 14px 24px;
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  background: ${theme.colors.backgroundCover.default};
`;
